import React, { useState, useEffect } from 'react';
import { useFormDepartureContext, isOptionalField } from './FormDepartureContext';
import { SNL_BRAND } from '../data/map';
import { cssUrl } from '../utils/cssVersion';

/* ============================================================================
 * FormDepartureWizard — South Yarra ticket wizard shell.
 * Same step flow/validation as the workflow app's FormWizard, restyled to the
 * dark ob-* look (newuseronboarding.css + filterTemplate.css). Logo and accent
 * come from the brand the page resolves (client brand → SNL default).
 * ==========================================================================*/

export const formFlow = [
  { id: 'personal-info-section', path: '/personal',     nextId: 'job-info-section',     continueText: 'Continue to Ticket Information', stepNumber: 1, stepName: 'Personal Information', fields: ['company', 'requestName', 'email'] },
  { id: 'job-info-section',      path: '/job',          nextId: 'dates-info-section',   continueText: 'Continue to Date Information',   stepNumber: 2, stepName: 'Ticket Information',   fields: ['jobTitle', 'jobType', 'jobDescription'] },
  { id: 'dates-info-section',    path: '/date-check',   nextId: 'upload-photo-section', continueText: 'Continue to Upload Photos',      stepNumber: 3, stepName: 'Dates Information',    fields: ['completedDate', 'completedTime'] },
  { id: 'upload-photo-section',  path: '/photo-upload', nextId: 'final-submit-section', continueText: 'Continue to Review & Submit',    stepNumber: 4, stepName: 'Photo Uploads',        fields: ['photos'] },
  { id: 'final-submit-section',  path: '/review',       nextId: null,                   continueText: 'Submit',                         stepNumber: 5, stepName: 'Review & Submit',      fields: [] },
];

const FormDepartureWizard = ({ children }) => {
  const {
    currentStep,
    setCurrentStep,
    formData,
    formErrors,
    isSubmitting,
    submitMessage,
    setModifiedFields,
    setFormErrors,
    brand,
  } = useFormDepartureContext();

  const theme  = brand || SNL_BRAND;
  const ACCENT = theme.accent;
  const LOGO   = theme.logoDark || theme.logo;

  const [showLoadingTransition, setShowLoadingTransition] = useState(true);

  // Stylesheets — versioned via cssUrl() → /static/reactcss/<file>?v=<mtime>
  useEffect(() => {
    const links = ['newuseronboarding.css', 'filterTemplate.css'].map((f) => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = cssUrl(f);
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => document.head.removeChild(l));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowLoadingTransition(false), 800);
    return () => clearTimeout(t);
  }, []);

  const getCurrentStep = () => formFlow[currentStep];

  const canProceedToNextStep = () => {
    const step = getCurrentStep();
    const fields = step.fields;

    setModifiedFields((prev) => {
      const s = new Set(prev);
      fields.forEach((f) => s.add(f));
      return s;
    });

    const newErrors = { ...formErrors };

    if (step.id === 'job-info-section') {
      if (!formData.jobTitle) newErrors.jobTitle = 'Ticket Title is required';
      if (!formData.jobType) newErrors.jobType = 'Ticket Type is required';
      if (!formData.jobDescription) newErrors.jobDescription = 'Ticket Description is required';
    } else if (step.id === 'dates-info-section') {
      if (!formData.completedDate) newErrors.completedDate = 'Completed Date is required';
      if (!formData.completedTime) newErrors.completedTime = 'Completed Time is required';
    } else if (step.id === 'upload-photo-section') {
      if (!formData.photos || formData.photos.length === 0) {
        newErrors.photos = 'At least one photo is required for the ticket';
      }
    } else {
      fields.forEach((field) => {
        if (isOptionalField(field, formData)) return;
        if (!formData[field] && !newErrors[field]) {
          newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        }
      });
    }

    if (JSON.stringify(newErrors) !== JSON.stringify(formErrors)) setFormErrors(newErrors);
    return !fields.some((f) => !!newErrors[f]);
  };

  const handleNext = () => {
    if (!canProceedToNextStep()) return;
    const next = currentStep + 1;
    if (next < formFlow.length) setCurrentStep(next);
  };

  const progress = ((currentStep + 1) / formFlow.length) * 100;
  const step = getCurrentStep();

  if (showLoadingTransition) {
    return (
      <div className="ob-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <img src={LOGO} alt={theme.label} style={{ width: 96, height: 96, objectFit: 'contain', margin: '0 auto 12px' }} />
          <p className="ob-subtitle">Loading your ticket form…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ob-page">
      <div className="ob-container">
        <div className="ob-header" style={{ position: 'relative' }}>
          <img
            src={LOGO}
            alt={theme.label}
            style={{ width: 84, height: 84, objectFit: 'contain', display: 'block', margin: '0 auto 8px' }}
          />
          <h1 className="ob-title">New Ticket Request</h1>
          <p className="ob-subtitle">
            Welcome, <span style={{ color: ACCENT, fontWeight: 600 }}>{formData.requestName}</span>
            {formData.company ? ` from ${formData.company}` : ''}. Complete each section — you'll review everything before submitting.
          </p>
        </div>

        <div className="ob-form-card">
          {/* Progress */}
          <div style={{ padding: '4px 0 14px' }}>
            <div style={{ width: '100%', height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }}>
              <div style={{ width: `${progress}%`, height: '100%', borderRadius: 999, transition: 'width .3s ease',
                background: `linear-gradient(90deg, ${ACCENT}, #fb923c)` }} />
            </div>
            <p className="ob-subtitle" style={{ marginTop: 8, marginBottom: 0 }}>
              Step {step.stepNumber} of {formFlow.length}: <span style={{ color: ACCENT, fontWeight: 600 }}>{step.stepName}</span>
            </p>
          </div>

          {isSubmitting && (
            <div className="ob-record-card" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#4ade80', borderColor: 'rgba(74,222,128,0.4)' }}>
              <span className="ob-spinner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </span>
              Submitting your ticket…
            </div>
          )}

          {submitMessage && (
            <div className={`ob-toast ${submitMessage.type === 'error' ? 'ob-toast-error' : 'ob-toast-success'}`}
              style={{ position: 'static', margin: '0 0 12px', transform: 'none' }}>
              {submitMessage.message}
            </div>
          )}

          <div className="ob-form-fields" style={{ minHeight: '40vh', maxHeight: '70vh' }}>
            {children}

            {step.nextId !== null && (
              <button type="button" className="ob-btn ob-btn-submit" onClick={handleNext} style={{ marginTop: 16 }}>
                {step.continueText}
              </button>
            )}
          </div>
        </div>

        <p className="ob-footer">{theme.label} · Online Communications Portal</p>
      </div>
    </div>
  );
};

export default FormDepartureWizard;